import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, FormHelperText, TextField, Button } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useQueries } from 'react-query';
import { getFrameworks, getControlsGrouped, postGapAnalysis } from 'services/gap-analysis';
import { Card } from 'Components/Card';
import ProgressStepper from 'Components/ProggessStepper';
import { Container } from 'Components/Container';
import React, { useEffect, useState, useMemo } from 'react';
import FrameworkSelector from 'Components/FrameworkSelector';
import DomainControlsList from 'Components/DomainControlsList';
import { useNavigate } from 'react-router-dom';
import { PageHelper } from 'Components/PageHelper';

export const GapAnalysisForm = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [controls, setControls] = useState([]);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    unregister,
  } = useForm({
    defaultValues: {
      company_id: selectedTenant._id || '',
      asset_id: '',
      name: '',
      description: '',
      type: 'company',
      frameworks: [],
    },
  });
  useEffect(() => {
    if (selectedTenant) {
      setValue('company_id', selectedTenant._id);
    }
  }, [selectedTenant, setValue]);

  const results = useQueries([
    {
      queryKey: ['frameworks'],
      queryFn: getFrameworks,
    },
  ]);

  const frameworksData = results[0].data?.data || [];

  // 📌 Busca controles dinamicamente com base nos frameworks selecionados
  useEffect(() => {
    const fetchControlsGrouped = async frameworksIds => {
      try {
        if (frameworksIds.length === 0) {
          setControls([]); // Se não houver frameworks selecionados, limpa os controles
          return;
        }
        const result = await getControlsGrouped(frameworksIds);
        if (result.status === 200) {
          setControls(result.data);
        } else {
          console.error('Erro ao buscar controles');
        }
      } catch (error) {
        console.error('Erro ao buscar controles agrupados:', error);
      }
    };

    fetchControlsGrouped(selectedFrameworks.map(fw => fw._id));
  }, [selectedFrameworks]);

  useEffect(() => {
    if (step === 1) {
      register('frameworks', {
        validate: value => (Array.isArray(value) && value.length > 0) || t('gap-analysis:form.validation.frameworkRequired'),
      });

      if (!watch('frameworks') || watch('frameworks').length === 0) {
        setValue(
          'frameworks',
          selectedFrameworks.map(fw => fw._id),
        );
      }
    } else if (step === 0 && selectedFrameworks.length === 0) {
      unregister('frameworks');
    }
  }, [register, unregister, step, setValue, watch, selectedFrameworks]);

  // 📌 Seleciona/Deseleciona Frameworks
  const handleToggle = framework => {
    setSelectedFrameworks(prevSelected => {
      const isSelected = prevSelected.some(item => item._id === framework._id);
      const updatedSelected = isSelected
        ? prevSelected.filter(item => item._id !== framework._id)
        : [...prevSelected, framework];

      setValue(
        'frameworks',
        updatedSelected.map(fw => fw._id),
      );
      trigger('frameworks');

      return updatedSelected;
    });
  };

  // 📌 Envia os dados para cadastrar a Análise
  const submitGapAnalysis = handleSubmit(async data => {
    try {
      const payload = {
        ...data,
        framework_ids: selectedFrameworks.map(fw => fw._id),
      };
      delete payload.frameworks;
      const response = await postGapAnalysis(payload);

      const analysisId = response.data?._id || response.data?.id;
      if (response.status === 201 || response.status === 200) {
        if (analysisId) {
          navigate(`/gap-analysis/show/${analysisId}`);
        }
      }
    } catch (error) {
      alert(t('gap-analysis:form.saveError'));
    }
  });

  const renderStep1 = () => {
    return (
      <Box component="form">
        <FormControl fullWidth margin="normal" error={!!errors.name}>
          <div data-tour="gap-name">
          <TextField label={t('gap-analysis:form.fields.name')} {...register('name', { required: t('gap-analysis:form.validation.nameRequired') })} />
          {errors.name && <FormHelperText>{errors.name.message}</FormHelperText>}
          </div>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <div data-tour="gap-description">
          <TextField label={t('gap-analysis:form.fields.description')} {...register('description')} multiline rows={3} />
          </div>
        </FormControl>
      </Box>
    );
  };
  const renderStep2 = () => (
    <FormControl fullWidth margin="normal" error={!!errors.frameworks}>
      <div data-tour="framework-selector">
      <FrameworkSelector
        frameworks={frameworksData}
        selectedFrameworks={selectedFrameworks}
        handleToggle={handleToggle}
      />
      {errors.frameworks && <FormHelperText>{errors.frameworks.message}</FormHelperText>}
      </div>
    </FormControl>
  );

  const renderStep3 = () => <div data-tour="controls-list"><DomainControlsList data={controls} /></div>;

  const renderReview = () => {
    return (
      <Box>
        <div data-tour="review-summary">
        <h3>{t('gap-analysis:form.summary.title')}</h3>
        <p>
          <strong>{t('gap-analysis:form.summary.nameLabel')}</strong> {watch('name') || t('gap-analysis:form.summary.notInformed')}
        </p>
        <p>
          <strong>{t('gap-analysis:form.summary.descriptionLabel')}</strong> {watch('description') || t('gap-analysis:form.summary.notInformed')}
        </p>

        <p>
          <strong>{t('gap-analysis:form.summary.frameworksLabel')}</strong>
        </p>
        {selectedFrameworks.length > 0 ? (
          <ul>
            {selectedFrameworks.map(fw => (
              <li key={fw._id}>
                {fw.sourceIdentifier} ({t('gap-analysis:form.frameworks.controlsCount', { count: fw.controlCount ?? 0 })})
              </li>
            ))}
          </ul>
        ) : (
          <p>{t('gap-analysis:form.summary.noFrameworks')}</p>
        )}
        </div>

        <div data-tour="save-button">
        <Button variant="contained" color="primary" onClick={submitGapAnalysis}>
          {t('gap-analysis:form.saveButton')}
        </Button>
        </div>
      </Box>
    );
  };
  const stepsContent = [
    renderStep1({ watch, errors, register }),
    renderStep2({ frameworksData, selectedFrameworks, handleToggle }),
    renderStep3(),
    renderReview({ watch, selectedFrameworks, submitGapAnalysis }),
  ];
  const steps = [
    t('gap-analysis:form.steps.step1'),
    t('gap-analysis:form.steps.step2'),
    t('gap-analysis:form.steps.step3'),
    t('gap-analysis:form.steps.step4'),
  ];

  const tourSteps = useMemo(() => [
    { target: 'body', content: t('gap-analysis:form.tour.step0'), disableBeacon: true, disableScrolling: true, placement: 'center' },
    { target: '[data-tour="page-title"]', content: t('gap-analysis:form.tour.step1'), disableScrolling: true },
    { target: '[data-tour="stepper-card"]', content: t('gap-analysis:form.tour.step2'), disableScrolling: false },
    { target: '[data-tour="gap-name"]', content: t('gap-analysis:form.tour.step3'), disableScrolling: false },
    { target: '[data-tour="gap-description"]', content: t('gap-analysis:form.tour.step4'), disableScrolling: false },
    { target: 'body', content: t('gap-analysis:form.tour.step5'), disableScrolling: true, placement: 'center' },
    { target: 'body', content: t('gap-analysis:form.tour.step6'), disableScrolling: true, placement: 'center' },
    { target: 'body', content: t('gap-analysis:form.tour.step7'), disableScrolling: true, placement: 'center' },
    { target: 'body', content: t('gap-analysis:form.tour.step8'), disableScrolling: true, placement: 'center' },
  ], [t]);

  return (
    <Container title={t('gap-analysis:page.addTitle')} breadcrumb={t('gap-analysis:page.breadcrumb')} linkPage="/gap-analysis" data-tour="page-title">
      <Card sx={'mt-4'} data-tour="stepper-card">
        <ProgressStepper
          steps={steps}
          activeStep={step}
          data-tour="progress-stepper"
          onNext={async () => {
            let isValid = await trigger();
            if (step === 1) {
              const frameworksValid = await trigger('frameworks');
              isValid = isValid && frameworksValid; // Só avança se os frameworks estiverem válidos
            }
            if (isValid) {
              setStep(prev => prev + 1);
            }
          }}
          onBack={() => setStep(prev => prev - 1)}
          stepsContent={stepsContent}
        />
      </Card>
      <PageHelper steps={tourSteps} tourKey="gap-analysis-form" />
    </Container>
  );
};

